import {
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useRef } from 'react';
import { checkboxMenuColors, checkboxMenuTriggerMetrics } from '../ui/primitives/CheckboxMenu';

/** Shared width for list-page local search (Home, Orders, Invoicing, Exports, …). */
export const LIST_PAGE_SEARCH_WIDTH = '260px';

interface CollapsibleSearchProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    isCompact: boolean;
    width?: string;
    truncatePlaceholder?: boolean;
}

const placeholderEllipsisSx = {
    '&::placeholder': {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
};

const CollapsibleSearch = ({
    value,
    onChange,
    placeholder = 'Search...',
    isCompact,
    width = LIST_PAGE_SEARCH_WIDTH,
    truncatePlaceholder = false,
}: CollapsibleSearchProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const placeholderSx = truncatePlaceholder ? placeholderEllipsisSx : undefined;

    if (!isCompact) {
        return (
            <InputGroup size="md" w={width}>
                <InputLeftElement pointerEvents="none" h={checkboxMenuTriggerMetrics.minH}>
                    <SearchIcon color={checkboxMenuColors.icon} boxSize={3.5} />
                </InputLeftElement>
                <Input
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    bg="white"
                    borderColor={checkboxMenuColors.popoverBorder}
                    borderRadius={checkboxMenuTriggerMetrics.borderRadius}
                    h={checkboxMenuTriggerMetrics.minH}
                    minH={checkboxMenuTriggerMetrics.minH}
                    fontSize={checkboxMenuTriggerMetrics.fontSize}
                    fontWeight={checkboxMenuTriggerMetrics.fontWeight}
                    color={checkboxMenuColors.input}
                    _placeholder={{
                        color: checkboxMenuColors.muted,
                        fontSize: checkboxMenuTriggerMetrics.fontSize,
                        fontWeight: checkboxMenuTriggerMetrics.fontWeight,
                    }}
                    _hover={{ borderColor: checkboxMenuColors.popoverBorder }}
                    _focus={{ borderColor: checkboxMenuColors.popoverBorder, boxShadow: 'none' }}
                    _focusVisible={{ borderColor: checkboxMenuColors.popoverBorder, boxShadow: 'none' }}
                    sx={placeholderSx}
                />
            </InputGroup>
        );
    }

    return (
        <Popover placement="bottom-end" initialFocusRef={inputRef}>
            <PopoverTrigger>
                <IconButton
                    aria-label="Search"
                    title="Search"
                    icon={<SearchIcon boxSize={3.5} />}
                    size="sm"
                    h={checkboxMenuTriggerMetrics.minH}
                    w={checkboxMenuTriggerMetrics.minH}
                    variant="outline"
                    borderColor={checkboxMenuColors.popoverBorder}
                    color={checkboxMenuColors.muted}
                    borderRadius={checkboxMenuTriggerMetrics.borderRadius}
                    _hover={{ bg: 'gray.50' }}
                    _active={{ bg: 'gray.100' }}
                />
            </PopoverTrigger>
            <PopoverContent w={width} boxShadow="none" borderRadius="md">
                <PopoverBody p={2}>
                    <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" boxSize={3} />
                        </InputLeftElement>
                        <Input
                            ref={inputRef}
                            placeholder={placeholder}
                            value={value}
                            onChange={onChange}
                            bg="white"
                            borderColor={checkboxMenuColors.popoverBorder}
                            borderRadius={checkboxMenuTriggerMetrics.borderRadius}
                            h={checkboxMenuTriggerMetrics.minH}
                            fontSize={checkboxMenuTriggerMetrics.fontSize}
                            fontWeight={checkboxMenuTriggerMetrics.fontWeight}
                            color={checkboxMenuColors.input}
                            _placeholder={{
                                color: checkboxMenuColors.muted,
                                fontSize: checkboxMenuTriggerMetrics.fontSize,
                                fontWeight: checkboxMenuTriggerMetrics.fontWeight,
                            }}
                            _focus={{ borderColor: checkboxMenuColors.popoverBorder, boxShadow: 'none' }}
                            sx={placeholderSx}
                        />
                    </InputGroup>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default CollapsibleSearch;
